/**
 * Moteur `deal-2026` — opération d'achat-revente (marchand de biens), décision D10.
 *
 * Volontairement **séparé de `mmi-2026`** : aucune fiscalité des particuliers (revenus fonciers,
 * plus-value immobilière, résidence fiscale) n'entre ici. La fiscalité est celle d'une société
 * soumise à l'IS, avec une TVA éventuelle sur la revente.
 *
 * Le bilan reprend le simulateur de rentabilité de la formation Arthaud (M6), formule par formule :
 * marge = CA − commission de revente − (acquisition + travaux + portage), rapportée au coût de
 * revient. Ce que le classeur ne fait pas (M-D1, M-D2) est ajouté ici : TVA sur marge puis IS.
 */
import { corporateTax } from './tax/fr/regimes.js';
export const ENGINE_DEAL_2026 = 'deal-2026';
/** Droits d'enregistrement et frais d'acte, en part du prix d'acquisition (classeur, cellule C23). */
export const DEAL_REGISTRATION_RATES = {
    individual: 0.085,
    professional: 0.04,
};
/** Seuil de « bonne affaire » de la méthode : 20 % de marge sur le coût de revient, **avant** impôt. */
export const DEAL_MARGIN_TARGET = 0.2;
export const DEAL_SCENARIOS = ['pessimistic', 'realistic', 'optimistic'];
function solve(input, scenario) {
    const revenue = input.resalePrices[scenario];
    const registrationRate = input.registrationRate ?? DEAL_REGISTRATION_RATES[input.operator];
    const registrationFees = input.purchasePrice * registrationRate;
    const acquisitionCost = input.purchasePrice + registrationFees;
    const contingency = input.works * input.contingencyRate;
    const worksCost = input.works + contingency;
    // M-D5 : l'assiette des frais financiers est acquisition + travaux, non l'acquisition seule.
    const financingCost = (acquisitionCost + worksCost) * (1 - input.equityShare) * input.financingRate * (input.carryMonths / 12);
    const carryingCharges = input.monthlyCarryingCharges * input.carryMonths;
    const carryCost = financingCost + carryingCharges;
    const totalCost = acquisitionCost + worksCost + carryCost;
    const resaleCommission = revenue * input.commissionRate;
    const grossMargin = revenue - resaleCommission - totalCost;
    // TVA sur marge : assiette = prix de revente − prix d'acquisition, tous deux TTC ; la TVA est
    // extraite de la marge (x × t/(1+t)) et jamais négative.
    const vat = input.vatRegime === 'margin'
        ? Math.max(0, revenue - input.purchasePrice) * (input.vatRate / (1 + input.vatRate))
        : 0;
    const taxableResult = grossMargin - vat;
    const tax = corporateTax(taxableResult, input.corporateTaxRate, input.reducedCorporateTaxRate);
    const netMargin = taxableResult - tax;
    return {
        scenario,
        revenue,
        pricePerM2: input.surfaceM2 > 0 ? revenue / input.surfaceM2 : 0,
        resaleCommission,
        registrationFees,
        acquisitionCost,
        worksCost,
        contingency,
        financingCost,
        carryingCharges,
        carryCost,
        totalCost,
        grossMargin,
        grossMarginRate: totalCost > 0 ? grossMargin / totalCost : 0,
        meetsTarget: totalCost > 0 && grossMargin / totalCost >= DEAL_MARGIN_TARGET,
        vat,
        taxableResult,
        corporateTax: tax,
        netMargin,
        netMarginRate: totalCost > 0 ? netMargin / totalCost : 0,
    };
}
export function analyzeDeal(input) {
    return {
        engine: ENGINE_DEAL_2026,
        scenarios: {
            pessimistic: solve(input, 'pessimistic'),
            realistic: solve(input, 'realistic'),
            optimistic: solve(input, 'optimistic'),
        },
    };
}
//# sourceMappingURL=deal.js.map